/* eslint-disable react-refresh/only-export-components -- módulo de rutas: declara el árbol, no se recarga en caliente como un componente */
import { lazy, Suspense, type ComponentType } from "react";
import { createRoutesFromElements, Navigate, Outlet, Route, useLocation } from "react-router-dom";
import { AppShell } from "@/components/layout/AppShell";
import { ErrorState } from "@/components/states/ErrorState";
import { PageLoading } from "@/components/states/PageLoading";
import { LoginPage } from "@/routes/LoginPage";
import { NotFoundPage } from "@/routes/NotFoundPage";
import { ClienteScope, InicioRedirect, RedireccionAntigua, SoloAgencia } from "@/routes/RutasDeCliente";
import { useClienteActivo } from "@/hooks/useClienteActivo";
import { REDIRECCIONES_ANTIGUAS, redireccionDeLogin } from "@/routes/legacyRedirects";

const lazyPage = <K extends string>(load: () => Promise<Record<K, ComponentType>>, name: K) =>
  lazy(() => load().then((m) => ({ default: m[name] })));

const InicioPage = lazyPage(() => import("@/routes/InicioPage"), "InicioPage");
const CitasPage = lazyPage(() => import("@/routes/CitasPage"), "CitasPage");
const LeadsPage = lazyPage(() => import("@/routes/LeadsPage"), "LeadsPage");
const WhatsAppPage = lazyPage(() => import("@/routes/WhatsAppPage"), "WhatsAppPage");
const ExperienciasPage = lazyPage(() => import("@/routes/ExperienciasPage"), "ExperienciasPage");
const ExperienciaFichaPage = lazyPage(() => import("@/routes/ExperienciaFichaPage"), "ExperienciaFichaPage");
const MuestrasCalidadPage = lazyPage(() => import("@/routes/MuestrasCalidadPage"), "MuestrasCalidadPage");
const ExperienciasAjustesPage = lazyPage(() => import("@/routes/ExperienciasAjustesPage"), "ExperienciasAjustesPage");
const CarteraPage = lazyPage(() => import("@/routes/CarteraPage"), "CarteraPage");
const ResultadosTiendaPage = lazyPage(() => import("@/routes/ResultadosTiendaPage"), "ResultadosTiendaPage");
const CampanasPage = lazyPage(() => import("@/routes/CampanasPage"), "CampanasPage");
const CampanasFisicasPage = lazyPage(() => import("@/routes/CampanasFisicasPage"), "CampanasFisicasPage");
const PropuestasPage = lazyPage(() => import("@/routes/PropuestasPage"), "PropuestasPage");
const EmbudosPage = lazyPage(() => import("@/routes/EmbudosPage"), "EmbudosPage");
const EmbudoDetallePage = lazyPage(() => import("@/routes/EmbudosPage"), "EmbudoDetallePage");
const RegistroPage = lazyPage(() => import("@/routes/RegistroPage"), "RegistroPage");
const LaunchProposalPage = lazyPage(() => import("@/routes/LaunchProposalPage"), "LaunchProposalPage");
const PackagePreviewIndexPage = lazyPage(() => import("@/routes/PackagePreviewPage"), "PackagePreviewIndexPage");
const PackagePreviewPage = lazyPage(() => import("@/routes/PackagePreviewPage"), "PackagePreviewPage");
const LandingsPage = lazyPage(() => import("@/routes/LandingsPage"), "LandingsPage");
const LandingDetailPage = lazyPage(() => import("@/routes/LandingsPage"), "LandingDetailPage");
const ConexionesPage = lazyPage(() => import("@/routes/ConexionesPage"), "ConexionesPage");
const EquipoPage = lazyPage(() => import("@/routes/EquipoPage"), "EquipoPage");
const AjustesPage = lazyPage(() => import("@/routes/AjustesPage"), "AjustesPage");
const AjustesNegocioPage = lazyPage(() => import("@/routes/AjustesNegocioPage"), "AjustesNegocioPage");
const ReglasPage = lazyPage(() => import("@/routes/ReglasPage"), "ReglasPage");
const ClientesPage = lazyPage(() => import("@/routes/ClientesPage"), "ClientesPage");
const NuevoClientePage = lazyPage(() => import("@/routes/NuevoClientePage"), "NuevoClientePage");
const AgenciaEquipoPage = lazyPage(() => import("@/routes/AgenciaEquipoPage"), "AgenciaEquipoPage");
const InvitacionPage = lazyPage(() => import("@/routes/InvitacionPage"), "InvitacionPage");
// Spec 002 (mcp_oauth): consentimiento del AS, fuera del marco -- lo abre el navegador desde `/authorize`.
const ConsentimientoPage = lazyPage(() => import("@/routes/ConsentimientoPage"), "ConsentimientoPage");

/** Resultados: la agencia ve cuentas, campañas y gasto; la tienda, solo lo que ha traído (leads, citas y canjes). */
function Resultados() {
  const { rol } = useClienteActivo();
  return rol === "store" ? <ResultadosTiendaPage /> : <CarteraPage />;
}

function LoginRedirect() {
  const { search } = useLocation();
  return <Navigate to={redireccionDeLogin(search)} replace />;
}

function Raiz() {
  return (
    <Suspense fallback={<PageLoading />}>
      <Outlet />
    </Suspense>
  );
}

/**
 * Árbol de rutas del panel multicliente (interaction.md §7). Panel de observación: la preparación y la
 * ejecución viven en el chat MCP; las rutas antiguas solo redirigen para no romper marcadores ni avisos.
 */
export const appRoutes = createRoutesFromElements(
  <Route element={<Raiz />} errorElement={<ErrorState message="El panel ha tenido un problema inesperado. No se ha repetido ninguna operación." onRetry={() => window.location.reload()} />}>
    <Route path="/entrar" element={<LoginPage />} />
    <Route path="/login" element={<LoginRedirect />} />
    <Route path="/invitacion/:token" element={<InvitacionPage />} />
    <Route path="/oauth/autorizar" element={<ConsentimientoPage />} />
    <Route element={<AppShell />}>
      <Route path="/" element={<InicioRedirect />} />
      <Route element={<SoloAgencia />}>
        <Route path="/clientes" element={<ClientesPage />} />
        <Route path="/clientes/nuevo" element={<NuevoClientePage />} />
        <Route path="/agencia/equipo" element={<AgenciaEquipoPage />} />
      </Route>
      <Route path="/c/:cliente" element={<ClienteScope />}>
        <Route path="inicio" element={<InicioPage />} />
        <Route path="leads" element={<LeadsPage />} />
        <Route path="citas" element={<CitasPage />} />
        <Route path="whatsapp" element={<WhatsAppPage />} />
        <Route path="experiencias" element={<ExperienciasPage />} />
        <Route path="experiencias/solicitudes/:requestId" element={<ExperienciaFichaPage />} />
        <Route path="experiencias/muestras" element={<MuestrasCalidadPage />} />
        <Route path="experiencias/ajustes" element={<ExperienciasAjustesPage />} />
        <Route path="resultados" element={<Resultados />} />
        <Route path="campanas" element={<CampanasPage />} />
        <Route path="campanas/fisicas" element={<CampanasFisicasPage />} />
        <Route path="propuestas" element={<PropuestasPage />} />
        <Route path="propuestas/historial" element={<RegistroPage />} />
        <Route path="propuestas/embudos" element={<EmbudosPage />} />
        <Route path="propuestas/embudos/:funnelKey" element={<EmbudoDetallePage />} />
        <Route path="propuestas/lanzamiento/:slug" element={<LaunchProposalPage />} />
        {/* Ruta de comprobación T050/T051 (tasks.md) — pendiente de que T052 la enganche en la fila. */}
        <Route path="propuestas/paquete" element={<PackagePreviewIndexPage />} />
        <Route path="propuestas/paquete/:paqueteId" element={<PackagePreviewPage />} />
        <Route path="landings" element={<LandingsPage />} />
        <Route path="landings/:slug" element={<LandingDetailPage />} />
        <Route path="conexiones" element={<ConexionesPage />} />
        <Route path="equipo" element={<EquipoPage />} />
        <Route path="ajustes" element={<AjustesPage />} />
        <Route path="ajustes/negocio" element={<AjustesNegocioPage />} />
        <Route path="ajustes/avanzado" element={<ReglasPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
      {REDIRECCIONES_ANTIGUAS.map((redireccion) => (
        <Route key={redireccion.desde} path={redireccion.desde} element={<RedireccionAntigua redireccion={redireccion} />} />
      ))}
      <Route path="*" element={<NotFoundPage />} />
    </Route>
  </Route>,
);
